import { useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import {
  getArchetype,
  setArchetype,
  clearArchetype,
  type ArchetypeState,
} from '../lib/api';

interface ArchetypeRowProps {
  userId: string;
}

/**
 * Inline archetype display + override for a single user. Sits inside
 * SeasonSummaryCard.
 *
 * Reads the current state via GET /admin/user/{id}/archetype on mount.
 * Changing the dropdown POSTs the override; the "Reset" button DELETEs it
 * so the deterministic classifier takes over again on the next generation.
 *
 * Note: the override only affects FUTURE generations — it does not
 * retroactively rewrite the current season. The agent's get_archetype
 * tool reads the override on every loop, so the next /admin/build_user_season
 * or /admin/regen_season_magna_carta_v call will pick it up.
 */
export function ArchetypeRow({ userId }: ArchetypeRowProps) {
  const [state, setState] = useState<ArchetypeState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getArchetype(userId)
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : 'Failed to load archetype');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function handleChange(next: string) {
    if (!state || isSaving || next === state.archetype) return;
    setIsSaving(true);
    try {
      const updated = await setArchetype(userId, next);
      setState(updated);
      toast.success(`Archetype set to ${labelFor(next)}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to set archetype');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleReset() {
    if (!state || isSaving || !state.override) return;
    setIsSaving(true);
    try {
      const updated = await clearArchetype(userId);
      setState(updated);
      toast.success('Reverted to computed archetype');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reset archetype');
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 className="size-3.5 animate-spin" />
        Loading archetype…
      </div>
    );
  }

  if (!state) {
    return null; // Error toasted, nothing to render.
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">Archetype:</span>
      <select
        value={state.archetype}
        onChange={(e) => handleChange(e.target.value)}
        disabled={isSaving}
        className="px-2 py-0.5 border rounded-md bg-background text-sm font-medium disabled:opacity-60"
      >
        {state.valid_archetypes.map((a) => (
          <option key={a} value={a}>
            {labelFor(a)}
          </option>
        ))}
      </select>
      {state.source === 'override' ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-amber-700">
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium">Override</span>
          <button
            type="button"
            onClick={handleReset}
            disabled={isSaving}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title={`Computed: ${labelFor(state.computed_archetype)} (${state.rule_matched})`}
          >
            <RotateCcw className="size-3" />
            Reset to {labelFor(state.computed_archetype)}
          </button>
        </span>
      ) : (
        <span
          className="text-xs text-muted-foreground"
          title={`Confidence: ${state.confidence}`}
        >
          via {state.rule_matched}
        </span>
      )}
      {isSaving && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
    </div>
  );
}

// Human labels for the 5 canonical archetypes. The backend stores them as
// snake_case enum values; the UI shows TitleCase.
const ARCHETYPE_LABEL: Record<string, string> = {
  lifestyle: 'Lifestyle',
  bodybuilder: 'Bodybuilder',
  event_athlete: 'Event Athlete',
  sport_specific: 'Sport-Specific',
  hybrid: 'Hybrid',
};

function labelFor(value: string): string {
  return ARCHETYPE_LABEL[value] ?? value;
}

export { ARCHETYPE_LABEL, labelFor as labelForArchetype };
